import type {
  CompanionHomeModel,
  CompanionSavedPlace,
} from "@/lib/companion/home-model";
import { dayOutLocal, dayOutPlace, type DayOut } from "@/lib/companion/day-out";
import { intlTimeZoneAt } from "@/lib/time-zone-rules";

export type ManualLogSport = "ski" | "mtb" | "surf" | "coastal";

export type ManualLogSheetView = {
  title: "Log a day";
  sportLabel: "What";
  placeLabel: "Where";
  dateLabel: "When";
  durationLabel: "How long (optional)";
  submitLabel: "Add to my record";
  defaultSport: ManualLogSport;
  defaultPlaceSlug: string;
  defaultDate: string;
  minDate: string;
  maxDate: string;
  timeZone?: string;
  noteField: false;
  sportOptions: { label: string; value: ManualLogSport }[];
  placeOptions: { label: string; slug: string }[];
};

const MANUAL_LOG_LOOKBACK_DAYS = 14;

const MANUAL_LOG_SPORT_OPTIONS: ManualLogSheetView["sportOptions"] = [
  { label: "Ski or snowboard", value: "ski" },
  { label: "MTB", value: "mtb" },
  { label: "Surf", value: "surf" },
  { label: "Other water", value: "coastal" },
];

const MANUAL_LOG_STATIC_PLACES: ManualLogSheetView["placeOptions"] = [
  { label: "Morzine", slug: "morzine" },
  { label: "Val Thorens", slug: "val-thorens" },
  { label: "Fistral", slug: "newquay-fistral" },
  { label: "Croyde", slug: "croyde" },
  { label: "Lake Garda", slug: "lake-garda" },
];

export function manualLogSheetView(
  model: Pick<CompanionHomeModel, "visiblePlace" | "allSavedPlaces">,
  options: { now?: Date; timeZone?: string } = {},
): ManualLogSheetView {
  const placeOptions = manualLogPlaceOptions(model);
  return buildManualLogSheetView({
    defaultSport: manualLogSportForPlace(model.visiblePlace),
    placeOptions,
    now: options.now,
    timeZone: options.timeZone,
  });
}

export function manualLogSheetViewForPlace({
  placeSlug,
  placeLabel,
  defaultSport,
  savedPlaces = [],
  now,
  timeZone,
}: {
  placeSlug: string;
  placeLabel: string;
  defaultSport: ManualLogSport;
  savedPlaces?: readonly { label: string; slug: string }[];
  now?: Date;
  timeZone?: string;
}): ManualLogSheetView {
  return buildManualLogSheetView({
    defaultSport,
    placeOptions: [{ label: placeLabel, slug: placeSlug }, ...savedPlaces],
    now,
    timeZone,
  });
}

function buildManualLogSheetView({
  defaultSport,
  placeOptions,
  now = new Date(),
  timeZone,
}: {
  defaultSport: ManualLogSport;
  placeOptions: ManualLogSheetView["placeOptions"];
  now?: Date;
  timeZone?: string;
}): ManualLogSheetView {
  const defaultDate = localYmd(now, timeZone);
  const minDate = shiftedYmd(defaultDate, -MANUAL_LOG_LOOKBACK_DAYS);
  const dedupedPlaces = dedupePlaces([...placeOptions, ...MANUAL_LOG_STATIC_PLACES]);
  const defaultPlaceSlug = dedupedPlaces[0]?.slug ?? MANUAL_LOG_STATIC_PLACES[0].slug;

  return {
    title: "Log a day",
    sportLabel: "What",
    placeLabel: "Where",
    dateLabel: "When",
    durationLabel: "How long (optional)",
    submitLabel: "Add to my record",
    defaultSport,
    defaultPlaceSlug,
    defaultDate,
    minDate,
    maxDate: defaultDate,
    ...(timeZone ? { timeZone } : {}),
    noteField: false,
    sportOptions: MANUAL_LOG_SPORT_OPTIONS,
    placeOptions: dedupedPlaces,
  };
}

function manualLogPlaceOptions(
  model: Pick<CompanionHomeModel, "visiblePlace" | "allSavedPlaces">,
): ManualLogSheetView["placeOptions"] {
  const options: ManualLogSheetView["placeOptions"] = [];
  const addPlace = (
    place: Pick<CompanionSavedPlace, "name" | "slug" | "isFestival" | "sport"> | null,
  ) => {
    if (!place || place.isFestival || place.sport === "festival" || !place.slug) return;
    options.push({ label: place.name, slug: place.slug });
  };

  addPlace(model.visiblePlace);
  for (const place of model.allSavedPlaces) addPlace(place);
  return dedupePlaces(options);
}

function dedupePlaces(
  places: ManualLogSheetView["placeOptions"],
): ManualLogSheetView["placeOptions"] {
  const seen = new Set<string>();
  const options: ManualLogSheetView["placeOptions"] = [];
  for (const place of places) {
    if (!place.slug || seen.has(place.slug)) continue;
    seen.add(place.slug);
    options.push(place);
  }
  return options;
}

function manualLogSportForPlace(place: CompanionSavedPlace | null): ManualLogSport {
  const keys = [place?.preferredSport, place?.locationType, place?.sport].filter(
    (value): value is string => typeof value === "string",
  );

  for (const key of keys) {
    const sport = manualLogSportForSportKey(key);
    if (sport) return sport;
  }
  return "ski";
}

export function manualLogSportForSportKey(value: string | null | undefined): ManualLogSport | null {
  const key = value?.toLowerCase().trim() ?? "";
  if (!key) return null;
  if (key === "mtb" || key.includes("bike")) return "mtb";
  if (key === "surf") return "surf";
  if (
    key === "ski" ||
    key === "piste" ||
    key === "off-piste" ||
    key === "touring" ||
    key.includes("snow")
  ) {
    return "ski";
  }
  if (
    key === "coastal" ||
    key === "water" ||
    key === "wind" ||
    key === "foil" ||
    key === "downwind-foil" ||
    key === "wing-foil" ||
    key === "diving" ||
    key === "sup" ||
    key === "sup-touring" ||
    key === "sup-surf" ||
    key === "sup-downwind" ||
    key === "swim" ||
    key === "swimming" ||
    key === "bodyboarding" ||
    key.includes("sail")
  ) {
    return "coastal";
  }
  return null;
}

export function manualLogViewForDayOut(view: ManualLogSheetView, selection: DayOut | null, now: Date): ManualLogSheetView {
  if (!selection) return view;
  const place = dayOutPlace(selection);
  if (!place) return view;
  const selected = manualLogSheetViewForPlace({ placeSlug: place.slug, placeLabel: place.name,
    defaultSport: manualLogSportForSportKey(selection.sport) ?? view.defaultSport,
    savedPlaces: view.placeOptions, now, timeZone: selection.timezone });
  return { ...selected, defaultDate: dayOutLocal(selection).date };
}

function localYmd(date: Date, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    // tzdata 2026e's wall clock even on a runtime whose data predates it (lib/time-zone-rules.ts).
    ...(timeZone ? { timeZone: intlTimeZoneAt(timeZone, date.getTime()) } : {}),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value ?? "1970";
  const month = parts.find((part) => part.type === "month")?.value ?? "01";
  const day = parts.find((part) => part.type === "day")?.value ?? "01";
  return `${year}-${month}-${day}`;
}

function shiftedYmd(dateId: string, days: number): string {
  const [year, month, day] = dateId.split("-").map(Number);
  const shifted = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}
